// covered-by: frontends/carrie/src/routes/settings/routes/__tests__/EnterpriseSsoSettingsPanel.test.tsx
import {
  Button,
  Card,
  Flex,
  Heading,
  Select,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useEffect, useState } from "react";

import { useUI } from "../../../contexts/ui";
import {
  getEnterpriseAuth,
  updateEnterpriseAuth,
  type EnterpriseAuthPayload,
} from "../../../services/enterprises";

type SsoMode = "off" | "optional" | "enforced";

export function EnterpriseSsoSettingsPanel({
  enterpriseId,
}: {
  enterpriseId: string;
}) {
  const { notify } = useUI();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ssoMode, setSsoMode] = useState<SsoMode>("off");
  const [workosOrganizationId, setWorkosOrganizationId] = useState("");
  const [ssoDomains, setSsoDomains] = useState("");
  const [directorySyncEnabled, setDirectorySyncEnabled] = useState(false);
  // Per-enterprise MFA. Off by default for every enterprise. It governs password
  // sign-in, and it is NOT independent of the SSO mode: the requirement is
  // checked on every request that carries a session, and an SSO session proves
  // only that SSO was chosen where SSO is optional — so `optional` + required
  // MFA would refuse every SSO member of the tenant. The server rejects that
  // pair (`updateEnterpriseAuth`), which is why the toggle is disabled under
  // `optional` below. Under `enforced` the identity provider owns the second
  // factor and SSO members are exempt.
  const [mfaRequired, setMfaRequired] = useState(false);
  // What the server had when this panel loaded. The acknowledgement below
  // gates the off -> on transition against THIS, not against `mfaRequired`:
  // that value is hydrated from the server, so testing it would demand an
  // acknowledgement for an edit that never touched MFA. See `handleSave`.
  const [storedMfaRequired, setStoredMfaRequired] = useState(false);
  const [confirmMfa, setConfirmMfa] = useState(false);
  const [sessionMaxAgeDays, setSessionMaxAgeDays] = useState("");
  const [adminPortalUrl, setAdminPortalUrl] = useState<string | undefined>();
  const [confirmEnforce, setConfirmEnforce] = useState(false);

  // Under "optional" the toggle is disabled AND the API refuses the pair, so
  // the value this panel shows and sends is OFF whatever the state says. Both
  // halves matter. Showing it means the disabled control does not sit `checked`
  // while the request contradicts it. Sending it is what keeps the save from
  // dead-ending: an admin downgrading from enforced SSO to optional would
  // otherwise post the still-`checked` toggle's `true`, every save would 400 —
  // including one that only changed an SSO domain or the session age — and the
  // only control that could clear the field would be the disabled one. It also
  // self-heals a tenant already storing the pair (`auth.mfaRequired` shipped
  // before any auth path read it, so such rows exist).
  const effectiveMfaRequired = ssoMode === "optional" ? false : mfaRequired;

  // Turning the requirement ON is the destructive direction, so it is the one
  // that needs an acknowledgement. Both halves are derived from the EFFECTIVE
  // value and the stored baseline — the two facts the save actually weighs —
  // rather than from the raw toggle state, and they are read by the
  // acknowledgement's render condition as well as by `handleSave`.
  //
  // Deriving the render from the raw state is what left a stray acknowledgement
  // under a disabled toggle: on a tenant storing `{optional, mfaRequired: true}`
  // a save moves the baseline to the stored `false` while the hydrated raw
  // state stays `true`, so `mfaRequired && !storedMfaRequired` flipped true and
  // the confirm box rendered beneath a toggle that was unchecked and disabled.
  const enablingMfa = effectiveMfaRequired && !storedMfaRequired;
  const disablingMfa = !effectiveMfaRequired && storedMfaRequired;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getEnterpriseAuth(enterpriseId)
      .then((res) => {
        if (cancelled) return;
        const auth = res.auth;
        setSsoMode((auth.ssoMode as SsoMode) || "off");
        setWorkosOrganizationId(auth.workosOrganizationId || "");
        setSsoDomains((auth.ssoDomains || []).join(", "));
        setDirectorySyncEnabled(Boolean(auth.directorySyncEnabled));
        setMfaRequired(Boolean(auth.mfaRequired));
        setStoredMfaRequired(Boolean(auth.mfaRequired));
        setSessionMaxAgeDays(
          auth.sessionMaxAgeDays != null ? String(auth.sessionMaxAgeDays) : "",
        );
        setAdminPortalUrl(res.adminPortalUrl);
      })
      .catch(() => {
        if (cancelled) return;
        notify({
          variant: "error",
          title: "SSO settings",
          message: "Could not load enterprise auth settings.",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enterpriseId, notify]);

  async function handleSave() {
    if (ssoMode === "enforced" && !confirmEnforce) {
      notify({
        variant: "error",
        title: "Confirm enforcement",
        message:
          "Check the confirmation box before enforcing SSO. Password login will be blocked for members.",
      });
      return;
    }
    // `enablingMfa` / `disablingMfa` are the component-level derivations above
    // — the same two values the acknowledgement's own render condition reads,
    // so the box is on screen exactly when this gate consults it.
    if (enablingMfa && !confirmMfa) {
      notify({
        variant: "error",
        title: "Confirm two-factor authentication",
        message:
          "Check the confirmation box before requiring two-factor authentication. Members who sign in with a password must set up an authenticator app at their next sign-in.",
      });
      return;
    }
    setSaving(true);
    try {
      const body: EnterpriseAuthPayload = {
        ssoMode,
        workosOrganizationId: workosOrganizationId.trim() || undefined,
        ssoDomains: ssoDomains
          .split(",")
          .map((d) => d.trim().toLowerCase())
          .filter(Boolean),
        directorySyncEnabled,
        mfaRequired: effectiveMfaRequired,
      };
      if (sessionMaxAgeDays.trim()) {
        body.sessionMaxAgeDays = Number(sessionMaxAgeDays);
      }
      await updateEnterpriseAuth(enterpriseId, body);
      const ssoMessage =
        ssoMode === "off"
          ? "SSO disabled — password login restored for this enterprise."
          : ssoMode === "optional"
            ? "Optional SSO enabled — password still works while users link SSO."
            : "SSO enforced — members must use the identity provider.";
      notify({
        variant: "success",
        title: "SSO settings saved",
        // MFA is a second, independent policy, so it is named only when it is
        // what this save actually changed. Reporting it on every save would
        // claim the admin just changed MFA when they changed something else.
        message:
          ssoMessage +
          (enablingMfa
            ? " Two-factor authentication is now required for password sign-in."
            : disablingMfa
              ? " Two-factor authentication is no longer required for password sign-in."
              : ""),
      });
      setConfirmEnforce(false);
      setConfirmMfa(false);
      // The saved value is the new baseline: the next save of an unrelated
      // field must not re-ask, and re-enabling after this must. The effective
      // value, not the toggle's state — under "optional" this save stored OFF.
      setStoredMfaRequired(effectiveMfaRequired);
    } catch (err) {
      notify({
        variant: "error",
        title: "SSO settings",
        message:
          err instanceof Error ? err.message : "Failed to update SSO settings.",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card data-testid="settings-enterprise-sso-card">
      <Flex direction="column" gap="3" p="4">
        <Heading as="h2" size="4" weight="medium">
          Single sign-on (SSO)
        </Heading>
        <Text size="2" color="gray">
          Per-enterprise WorkOS SSO. Default is off (unchanged password login).
          Use optional while users link accounts, then enforce when ready.
          Rolling back from enforced to optional restores password login.
        </Text>

        {loading ? (
          <Text size="2" data-testid="settings-enterprise-sso-loading">
            Loading…
          </Text>
        ) : (
          <>
            <label htmlFor="enterprise-sso-mode">
              <Text as="div" size="2" mb="1" weight="bold">
                SSO mode
              </Text>
              <Select.Root
                value={ssoMode}
                onValueChange={(v) => {
                  const next = v as SsoMode;
                  setSsoMode(next);
                  // Clearing the acknowledgement when leaving enforced ensures
                  // re-selecting enforced always requires a fresh confirmation
                  // instead of silently reusing a stale checked box.
                  if (next !== "enforced") setConfirmEnforce(false);
                }}
              >
                <Select.Trigger
                  id="enterprise-sso-mode"
                  data-testid="settings-enterprise-sso-mode"
                  aria-label="SSO mode"
                />
                <Select.Content>
                  <Select.Item value="off">Off (password only)</Select.Item>
                  <Select.Item value="optional">
                    Optional (password + SSO)
                  </Select.Item>
                  <Select.Item value="enforced">
                    Enforced (SSO only)
                  </Select.Item>
                </Select.Content>
              </Select.Root>
            </label>

            <label htmlFor="enterprise-sso-org">
              <Text as="div" size="2" mb="1" weight="bold">
                WorkOS organization ID
              </Text>
              <TextField.Root
                id="enterprise-sso-org"
                data-testid="settings-enterprise-sso-org"
                aria-label="WorkOS organization ID"
                value={workosOrganizationId}
                onChange={(e) => setWorkosOrganizationId(e.target.value)}
                placeholder="org_…"
              />
            </label>

            <label htmlFor="enterprise-sso-domains">
              <Text as="div" size="2" mb="1" weight="bold">
                SSO domains (comma-separated)
              </Text>
              <TextField.Root
                id="enterprise-sso-domains"
                data-testid="settings-enterprise-sso-domains"
                aria-label="SSO domains"
                value={ssoDomains}
                onChange={(e) => setSsoDomains(e.target.value)}
                placeholder="acme.com, acme.co.uk"
              />
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="settings-enterprise-sso-dsync"
                aria-label="Enable directory sync"
                checked={directorySyncEnabled}
                onChange={(e) => setDirectorySyncEnabled(e.target.checked)}
              />
              <Text size="2">Directory Sync (SCIM) enabled</Text>
            </label>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  data-testid="settings-enterprise-sso-mfa"
                  aria-label="Require two-factor authentication"
                  // The effective value, so a disabled control shows what a
                  // save would actually send rather than a `checked` state the
                  // request contradicts.
                  checked={effectiveMfaRequired}
                  // Refused by the API under `optional` (see the state comment
                  // above), so it is not offered here either rather than
                  // surfacing a 400 after the admin has already committed the
                  // change in their head.
                  disabled={ssoMode === "optional"}
                  onChange={(e) => {
                    setMfaRequired(e.target.checked);
                    // Turning the requirement back off retires the
                    // acknowledgement, so re-enabling always needs a fresh one.
                    if (!e.target.checked) setConfirmMfa(false);
                  }}
                />
                <Text size="2">
                  Require two-factor authentication (authenticator app)
                </Text>
              </label>
              <Text size="2" color="gray" className="mt-1 block">
                {ssoMode === "optional"
                  ? 'Not available while SSO is optional: an SSO session then proves only that SSO was chosen, so the requirement would lock out your SSO members. Set SSO to "Enforced" to require MFA — the identity provider then handles the second factor.'
                  : "Members sign in with a password plus a code from an authenticator app. Members who sign in with SSO are not affected — their identity provider handles MFA."}
              </Text>
            </div>

            {enablingMfa ? (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  data-testid="settings-enterprise-sso-confirm-mfa"
                  aria-label="Confirm requiring two-factor authentication"
                  checked={confirmMfa}
                  onChange={(e) => setConfirmMfa(e.target.checked)}
                />
                <Text size="2">
                  I understand members who sign in with a password must set up
                  an authenticator app to keep working.
                </Text>
              </label>
            ) : null}

            <label htmlFor="enterprise-sso-session-days">
              <Text as="div" size="2" mb="1" weight="bold">
                Session max age (days, optional)
              </Text>
              <TextField.Root
                id="enterprise-sso-session-days"
                data-testid="settings-enterprise-sso-session-days"
                aria-label="Session max age in days"
                value={sessionMaxAgeDays}
                onChange={(e) => setSessionMaxAgeDays(e.target.value)}
                placeholder="e.g. 14"
                type="number"
                min={1}
              />
            </label>

            {ssoMode === "enforced" ? (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  data-testid="settings-enterprise-sso-confirm-enforce"
                  aria-label="Confirm SSO enforcement"
                  checked={confirmEnforce}
                  onChange={(e) => setConfirmEnforce(e.target.checked)}
                />
                <Text size="2">
                  I understand password login will be blocked for members until
                  rollback to optional/off.
                </Text>
              </label>
            ) : null}

            <Flex gap="3" wrap="wrap">
              <Button
                data-testid="settings-enterprise-sso-save"
                aria-label="Save SSO settings"
                loading={saving}
                onClick={() => void handleSave()}
              >
                Save SSO settings
              </Button>
              {adminPortalUrl ? (
                <a
                  href={adminPortalUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="settings-enterprise-sso-portal"
                  aria-label="Configure SSO in WorkOS Admin Portal"
                  className="inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium"
                >
                  Configure SSO (Admin Portal)
                </a>
              ) : null}
            </Flex>
          </>
        )}
      </Flex>
    </Card>
  );
}
